export const MCP_SERVER_URL = "https://productlane.com/api/mcp"
export const MCP_SERVER_NAME = "productlane"

export const MCP_DOCS_ARTICLE_ID = "935390b2-2de7-4757-a2eb-8bb7acba9e9c"

export type McpClientId =
	| "claude-code"
	| "cursor"
	| "vscode"
	| "claude"
	| "chatgpt"

export interface McpClient {
	id: McpClientId
	label: string
	hint: string
	command?: string
	deeplink?: string
	deeplinkLabel?: string
	steps?: string[]
}

// cursor://anysphere.cursor-deeplink/mcp/install takes the server config as base64 JSON.
export function cursorInstallDeeplink(
	serverUrl = MCP_SERVER_URL,
	name = MCP_SERVER_NAME,
) {
	const config = encodeToBase64(JSON.stringify({ url: serverUrl }))
	return `cursor://anysphere.cursor-deeplink/mcp/install?name=${encodeURIComponent(
		name,
	)}&config=${encodeURIComponent(config)}`
}

function encodeToBase64(value: string) {
	if (typeof btoa === "function") {
		return btoa(value)
	}
	return Buffer.from(value, "utf8").toString("base64")
}

export const MCP_CLIENTS: McpClient[] = [
	{
		id: "claude-code",
		label: "Claude Code",
		hint: "Run this in your terminal, then restart Claude Code.",
		command: `claude mcp add --transport http ${MCP_SERVER_NAME} ${MCP_SERVER_URL}`,
	},
	{
		id: "cursor",
		label: "Cursor",
		hint: "Cursor opens with the server prefilled. Authorize in the browser, then reload Cursor.",
		deeplink: cursorInstallDeeplink(),
		deeplinkLabel: "Add to Cursor",
	},
	{
		id: "vscode",
		label: "VS Code",
		hint: "Run this in your terminal, then reload VS Code.",
		command: `code --add-mcp '{"name":"${MCP_SERVER_NAME}","type":"http","url":"${MCP_SERVER_URL}"}'`,
	},
	{
		id: "claude",
		label: "Claude",
		hint: "For the Claude desktop and web apps.",
		steps: [
			"Open Settings, then Connectors.",
			"Choose Add custom connector.",
			"Paste the server URL and authorize.",
		],
	},
	{
		id: "chatgpt",
		label: "ChatGPT",
		hint: "Custom connectors live behind developer mode.",
		steps: [
			"Open Settings, then Connectors.",
			"Turn on developer mode under Advanced.",
			"Create a connector with the server URL.",
		],
	},
]

export function findMcpClient(id: McpClientId) {
	return MCP_CLIENTS.find((client) => client.id === id) ?? MCP_CLIENTS[0]!
}

const MCP_WORKSPACE_TASKS = `take a quick look at my workspace (is the help center empty, are there unanswered conversations, any recent commits) and recommend which of these to start with:
1. Import my existing data into Productlane. Ask me what I use today for documentation, changelog, and customer support. For each tool, use the first import path that works, in this order: (a) a native Productlane importer if one exists, such as Zendesk; (b) the tool's own MCP server, such as transferring docs from Mintlify through the Mintlify MCP; (c) export to CSV from the tool and use Productlane's CSV importer; (d) as a last resort, ask me for the tool's API key and write a script to import the data. Check https://productlane.com/docs for the available importers.
2. Generate the help center from the codebase.
3. Go through all conversations and draft a reply.
4. Generate a changelog draft from all changes in the codebase in the last two weeks.`

export const MCP_START_PROMPT = `The Productlane MCP server is now connected. First confirm the tools are available with a read-only call (list my threads or workspace info); if they aren't, help me finish connecting before continuing. Then ${MCP_WORKSPACE_TASKS}`

export const MCP_AGENT_SETUP_PROMPT = `Set up Productlane for me.

First, connect the Productlane MCP server. It runs at ${MCP_SERVER_URL} over HTTP; authorization happens in the browser and no API key is needed. If you can add MCP servers yourself, add it now. In Claude Code, run: claude mcp add --transport http ${MCP_SERVER_NAME} ${MCP_SERVER_URL}. In VS Code, run: code --add-mcp '{"name":"${MCP_SERVER_NAME}","type":"http","url":"${MCP_SERVER_URL}"}'. In any other client, tell me where to paste the server URL as a custom connector, then wait until I confirm it is connected.

Once connected, confirm the tools are available with a read-only call (list my threads or workspace info). Then ${MCP_WORKSPACE_TASKS}`

export const MCP_USE_CASES = [
	"Read, fix, and draft replies to conversations from your editor.",
	"Reproduce a bug report from the inbox and draft the fix as a PR.",
	"Draft, publish, and broadcast changelogs and help-center articles.",
]
